import { ArrowModes, LineStyles } from '../net/wire';
import type { ScanTool } from '../possession/ScanTool';
import type { Mode, ToolMode } from '../possession/ToolMode';
import type { ModeControlText, ModeDefinition, ModeRegistry, ReadoutOption } from './ModeDefinition';

/**
 * The single, dependency-free table of what each mode's trigger and stick axes say about
 * themselves — `ModeDefinition.ts`'s own doc on `ModeControlText` has the full reasoning for why
 * this is split out from the mode objects below rather than folded into them: `buildModeDefinitions`
 * needs live `toolMode`/`scan` instances to exist at all, but this text does not, which is what
 * lets `ControlBindings.ts` (in turn read by both the in-headset hint panel and the portal's
 * controls page, `PortalControls.ts`, which never has a session to build a real `ModeRegistry`
 * from) read it directly, with nothing constructed.
 *
 * `buildModeDefinitions` below spreads this table into each mode's own `label`/`triggerDescription`,
 * so this remains the one place any of that prose is actually written — not a second copy that
 * could drift from what `ControlBindings.ts` shows.
 *
 * `stickXDescription`/`stickYDescription` are `null` exactly where the mode's own `stickX`/
 * `stickY` axis binding (below) is `null` — `ModeDefinitions.test.ts` pins that the two agree,
 * since nothing in the type system connects a `ModeControlText` entry to the `ModeDefinition` it
 * describes.
 */
export const MODE_CONTROL_TEXT: Readonly<Record<Mode, ModeControlText>> = {
  build: {
    label: 'Build',
    triggerDescription: 'Grab, or take a shape.',
    stickXDescription: null,
    stickYDescription: null,
  },
  draw: {
    label: 'Draw',
    triggerDescription: 'Touch, or aim to draw.',
    stickXDescription: 'Ink colour (markers).',
    stickYDescription: 'Marker size, eraser.',
  },
  scan: {
    label: 'Scan',
    triggerDescription: 'Press, sweep, release.',
    stickXDescription: null,
    stickYDescription: 'Scan line length.',
  },
  link: {
    label: 'Link',
    // Contains "link" deliberately — ModeDefinitions.test.ts's "trigger description describes
    // the real gesture" pins that against the placeholder prose this replaced ("not yet
    // implemented") when Link mode was still a stub. "Drag between two nodes." (the shorter
    // phrase ControlBindings.ts used to hand-author) does not contain the word and would fail
    // that test — kept in mind here so a future edit does not silently reintroduce the mismatch.
    triggerDescription: 'Drag to link two nodes.',
    stickXDescription: 'Line style, incl. remove.',
    stickYDescription: 'Arrow direction.',
  },
};

/**
 * Link mode's own line-style axis, as a *set* — mirrors `ToolMode`'s own private
 * `LINK_LINE_SELECTIONS` (`cycleLinkStyle`'s own list): the three real `net/wire.ts` line
 * styles, then the UI-only `'remove'` position (`ToolMode.ts`'s own doc: "must never reach the
 * wire as a style"). Built from `LineStyles` rather than hand-typed strings — `'remove'` is not
 * a duplicate of any wire vocabulary member, since by design it explicitly is not one
 * (`ConnectionStyleDto.cs`'s own remarks; plan/mode-hierarchy.md, "Do not add that 'none'
 * position to LineStyles itself"). `ModeDefinitions.test.ts`'s own "readoutOptionRows tracks the
 * real line-style cycle" case steps `ToolMode.cycleLinkStyle` through every position and confirms
 * `readoutOptionRows()`'s own `selected` flag follows `toolMode.linkLineSelection` at each one —
 * a genuine guard against this list *missing or misnaming* a value `ToolMode`'s own private one
 * carries (a wrong or absent entry desyncs `selected` immediately, confirmed red before this
 * comment was written), since nothing in the type system connects the two lists.
 *
 * **What that test does not, and cannot, prove: the *order* below matches `ToolMode`'s own
 * cycle order.** `buildOptionRow` marks `selected` by value equality alone, so a value in the
 * wrong position here still reads correctly selected — only the wheel's own flanking items (what
 * sits one step to either side of the front one) would look wrong, and nothing in this headless
 * suite can see that (`NullEngine` never rasterizes — CLAUDE.md). The order here is written to
 * match `ToolMode`'s own `LINK_LINE_SELECTIONS` by hand, for exactly that reason; a headset pass
 * is what actually confirms flicking the stick moves the wheel the way it looks like it should.
 */
const LINK_LINE_OPTIONS: readonly string[] = [LineStyles.Solid, LineStyles.Dashed, LineStyles.Dotted, 'remove'];

/** Link mode's own arrow-direction axis, as a set — mirrors `ToolMode`'s own private
 *  `LINK_ARROW_MODES` (`cycleLinkArrow`'s own list), forward first to match `ArrowModes.Default`
 *  (`ConnectionStyleDto.cs`). See `LINK_LINE_OPTIONS`'s own doc for the reasoning this shares,
 *  including what is and is not actually proven about the order below. */
const LINK_ARROW_OPTIONS: readonly string[] = [ArrowModes.Forward, ArrowModes.Both, ArrowModes.None];

/** Builds one `readoutOptionRows` row from an ordered option list and the value currently
 *  selected — shared by link's own two axes below, rather than writing the same
 *  map-and-mark-selected shape twice. `danger` is set only for the UI-only `'remove'` position:
 *  the one item on either axis that destroys something instead of merely restyling it. */
function buildOptionRow(options: readonly string[], current: string): ReadoutOption[] {
  return options.map((value) => ({
    key: value,
    label: value,
    selected: value === current,
    danger: value === 'remove',
  }));
}

/**
 * Builds the mode registry — the single source of truth `ModeDefinition.ts`'s own doc
 * describes, constructed once per session, after `toolMode` and `scan` both exist (the same
 * "built once its dependencies are ready" lifetime `AuthoringController`'s own `yAxisActions`
 * table used to have — see this function's own call site).
 *
 * Deliberately a function, not a static table: unlike `NodeCatalog`, a mode's own axis
 * behaviour has to close over live per-session state (`toolMode`'s pen selection and marker
 * width, `scan`'s line length), so the registry cannot be authored as inert data the way the
 * node catalogue is — the same reason `ToolRegistry`'s own handlers close over live services
 * rather than being pure data all the way down. What stays true to the precedent is the
 * *shape*: everything downstream reads this one registry, keyed by `Mode`, rather than
 * re-deriving "what does this mode do" from scattered `if (mode === ...)` checks of its own.
 *
 * Adding a fifth mode is adding a case to the `Record` literal below — TypeScript's own
 * exhaustiveness check on `Record<Mode, ModeDefinition>` fails to compile otherwise, the same
 * guard `ControlBindings.ts`'s `MODE_BINDINGS` already uses for `Mode`. See
 * `ModeDefinitions.test.ts`, "a fifth mode needs only a definition," for the actual proof.
 */
export function buildModeDefinitions(deps: { toolMode: ToolMode; scan: ScanTool }): ModeRegistry {
  const { toolMode, scan } = deps;

  const build: ModeDefinition = {
    id: 'build',
    label: MODE_CONTROL_TEXT.build.label,
    offHandSurface: 'palette',
    readoutLines: () => [],
    readoutSwatches: () => [], // Build's own surface is the interactive palette, not a readout.
    readoutOptionRows: () => [], // Same reason — nothing here to show as a labelled carousel either.

    // The owner's own instruction, verbatim: "Build mode will not use the dominant stick,
    // everything there flows from the palette." Both axes stay null rather than wired to a
    // no-op — a null binding is what lets AuthoringController's dispatch tell "genuinely
    // unbound" apart from "bound to something that happened not to change" without a second
    // flag.
    stickX: null,
    stickY: null,

    // Link mode's own gesture landed for real (fca7ade) — the trigger here no longer aims or
    // connects at all, interim or otherwise: `ConnectionDrawer.tick` only runs while
    // `ToolMode.isLinkMode`, so Build's trigger is purely the near-reach grab (`GrabReach
    // .nodeWithinReach`) or taking an item off the tray. This used to say "(interim — Link
    // mode takes this over later)"; left uncorrected here until `ControlBindings.ts`'s own
    // identical staleness was found while rebuilding the control hints
    // (plan/ui-improvements.md item 6) is exactly the kind of drift that entry warns about.
    // The text itself now lives in MODE_CONTROL_TEXT.build.triggerDescription, above — read
    // from here rather than written a second time, which is the fix for the very staleness
    // this comment used to warn about (ControlBindings.ts used to hand-author its own,
    // differently-worded copy of this exact fact).
    triggerDescription: MODE_CONTROL_TEXT.build.triggerDescription,
    heldProp: 'none',
  };

  const draw: ModeDefinition = {
    id: 'draw',
    label: MODE_CONTROL_TEXT.draw.label,
    offHandSurface: 'readout',

    // Never a raw hex code here (the draw-variants task's own instruction) — colour is shown as
    // swatches instead (`readoutSwatches`, below). This is only ever the current tool variant's
    // own name.
    readoutLines: () => {
      const selection = toolMode.selection;

      if (!selection) {
        return ['draw'];
      }

      if (selection.kind === 'eraser') {
        return ['eraser'];
      }

      return [`${toolMode.markerWidth} marker`];
    },

    // One swatch per pen colour this whiteboard offers, the current one marked — empty while the
    // eraser is selected, since colour has nothing to act on there (plan/mode-hierarchy.md). This
    // is what replaces the old ink wheel's own raw-RGB-code overlay: a swatch is looked at, not
    // read, and "which one is current" is a marked flag on the row rather than a hex string.
    readoutSwatches: () => {
      const selection = toolMode.selection;

      if (selection?.kind !== 'pen') {
        return [];
      }

      return toolMode.penColors.map((color) => ({ color, selected: color === selection.color }));
    },

    readoutOptionRows: () => [], // Nothing state-shaped here — colour already has its own swatches.

    stickX: {
      // Colour lives on the bare stick now (no grip needed) — plan/mode-hierarchy.md moved it
      // off the dominant grip entirely once grip+stick was claimed for mode switching. Inert
      // while the eraser is selected: `cyclePenColor` itself no-ops in that state (ToolMode's
      // own doc), so nothing changes and this returns null — no flashed label for a press that
      // did nothing, the same "no green light for a change that didn't happen" rule every other
      // axis binding here follows.
      step: (direction) => {
        // `toolMode.selection` is a derived getter, freshly built on every read — comparing it
        // by reference would always read "changed," even for the no-op case, so this compares
        // the colour value itself instead.
        const before = toolMode.selection;
        const beforeColor = before?.kind === 'pen' ? before.color : null;
        toolMode.cyclePenColor(direction);
        const selection = toolMode.selection;

        if (selection?.kind !== 'pen' || selection.color === beforeColor) {
          return null;
        }

        // A position in the set, not the colour's own value — CLAUDE.md's own account of why
        // the old ink wheel's raw hex readout was the bug, not merely unpolished: "gives the
        // selection a sense of place by construction; a readout built on that mechanism has an
        // obvious 'you are here' to show that a bare colour swatch does not."
        const index = toolMode.penColors.indexOf(selection.color);
        return index === -1 ? null : `colour ${index + 1}/${toolMode.penColors.length}`;
      },
    },

    stickY: {
      // Four positions now, not three: slim/medium/fat marker, then the eraser — one ordered
      // list on the same axis, replacing the old marker-width-only cycle and the eraser's own
      // separate, stick-axis-less selectInk-only path at once (ToolMode.cycleDrawVariant's own
      // doc). This is what makes the eraser reachable again in VR after `1daa623` deleted the
      // off-hand ink wheel that used to be its only entry point.
      step: (direction) => {
        toolMode.cycleDrawVariant(direction);
        return toolMode.variant === 'eraser' ? 'eraser' : `${toolMode.markerWidth} marker`;
      },
    },

    triggerDescription: MODE_CONTROL_TEXT.draw.triggerDescription,
    heldProp: 'pen-or-eraser',
  };

  const scanMode: ModeDefinition = {
    id: 'scan',
    label: MODE_CONTROL_TEXT.scan.label,
    offHandSurface: 'readout',
    readoutLines: () => [`scan line ${scan.lineLength.toFixed(1)}m`],
    readoutSwatches: () => [], // Scan has nothing colour-shaped to show.
    readoutOptionRows: () => [], // Nor anything state-shaped for a labelled carousel.

    // "Dominant stick <-> | free" — plan/mode-hierarchy.md's own bindings table.
    stickX: null,

    stickY: {
      step: (direction) => {
        scan.adjustLineLength(direction);
        return `scan line ${scan.lineLength.toFixed(1)}m`;
      },
    },

    triggerDescription: MODE_CONTROL_TEXT.scan.triggerDescription,
    heldProp: 'scanner',
  };

  const link: ModeDefinition = {
    id: 'link',
    label: MODE_CONTROL_TEXT.link.label,
    offHandSurface: 'readout',

    // Empty, deliberately, unlike every other mode's own readoutLines: Link is the one mode with
    // a labelled option carousel (readoutOptionRows, below) that already shows the real
    // style/arrow selection visually, per-position — the owner's own words, on a headset, once
    // the carousel shipped: "We also don't need the floating labels over it when we have the
    // carousel." The text used to restate exactly what the carousel already shows
    // (`style: ${toolMode.linkStyleLabel}`, `arrow: ${toolMode.linkArrowLabel}`); removed here
    // rather than left stacked above a surface that makes it redundant. `ToolMode.linkStyleLabel`/
    // `linkArrowLabel` are still real, live getters — `stickX`/`stickY`'s own `step` callbacks
    // below still return prose built from them, for whatever transient feedback surface (if any)
    // reads a step's own return value; only the readout's own stacked text line is gone.
    readoutLines: () => [],
    readoutSwatches: () => [], // Nothing colour-shaped here either — not this task's scope.

    // The visual counterpart to the text lines just above, added after a headset session found
    // the text alone insufficient (`ReadoutOption`'s own doc: "It's impossible to know what you
    // have selected with the stick"). Two rows, one per axis: line style — the one carrying the
    // destructive `'remove'` position, flagged `danger` by `buildOptionRow` — and arrow
    // direction. Both read `toolMode.linkLineSelection`/`linkArrowMode` (the *values* the stick
    // actually cycles) rather than the prose getters (`linkStyleLabel`/`linkArrowLabel`, which
    // fold `'remove'`'s arrow into `'n/a'` for the text line) — a carousel item's own `selected`
    // flag has to match one of `LINK_LINE_OPTIONS`/`LINK_ARROW_OPTIONS` exactly, and `'n/a'` is
    // not a member of either list.
    readoutOptionRows: () => [
      buildOptionRow(LINK_LINE_OPTIONS, toolMode.linkLineSelection),
      buildOptionRow(LINK_ARROW_OPTIONS, toolMode.linkArrowMode),
    ],

    stickX: {
      step: (direction) => {
        toolMode.cycleLinkStyle(direction);
        return `style: ${toolMode.linkStyleLabel}`;
      },
    },

    stickY: {
      step: (direction) => {
        toolMode.cycleLinkArrow(direction);
        return `arrow: ${toolMode.linkArrowLabel}`;
      },
    },

    // The dominant trigger now runs the real gesture: aim at a node, pull, aim at another,
    // release — ConnectionDrawer.tick, gated on ToolMode.isLinkMode rather than the interim
    // "isToolActive means build's trigger is free" check every other non-build mode still
    // uses. ToolMode.isToolActive still reserves the trigger away from a grab in this mode
    // (mode !== 'build'), which is what stops a press here from ever falling through to one.
    triggerDescription: MODE_CONTROL_TEXT.link.triggerDescription,
    heldProp: 'none',
  };

  return { build, draw, scan: scanMode, link };
}

/** Re-exported so a caller only needs one import for both the type and the builder. */
export type {
  AxisBinding,
  ModeControlText,
  ModeDefinition,
  ModeRegistry,
  ReadoutOption,
  ReadoutSwatch,
} from './ModeDefinition';
